import type { ViewTypeValues } from './enums';
import type { ActionOption, ActionOptionCommon, ActionValues, NextStep, StepValues } from '@repo/common';

import { ActionType, Step } from '@repo/common';

import { ViewType } from './enums';

export function hasNextStep(
  actionOption: ActionOption,
): actionOption is Omit<ActionOptionCommon, 'nextStep'> & { nextStep: NextStep } {
  return !!actionOption.nextStep;
}

export function isStepSkippable(step: NextStep): boolean {
  if (step.action.type !== ActionType.OPTIONS) {
    return false;
  }

  return step.action.options.some((opt) => opt.type === ActionType.SKIP);
}

export type GetStepOptionsFilter =
  | {
      exact?: ActionValues;
      omit?: undefined;
    }
  | {
      exact?: undefined;
      omit?: ActionValues;
    };

export function getOptions(step: NextStep, filter: GetStepOptionsFilter = {}): ActionOption[] | undefined {
  if (step.action.type !== ActionType.OPTIONS) {
    return undefined;
  }

  const actionOptions = [...step.action.options];

  if (filter.exact) {
    return actionOptions.filter((i) => i.type === filter.exact);
  }

  if (filter.omit) {
    return actionOptions.filter((i) => i.type !== filter.omit);
  }

  return actionOptions;
}

export type GetStepActionFilter = {
  exact?: ActionValues;
};

export function getStepAction(step: NextStep, filter: GetStepActionFilter = {}): ActionOption | undefined {
  if (step.action.type === ActionType.OPTIONS) {
    return undefined;
  }

  if (filter.exact) {
    return step.action.type === filter.exact ? step.action : undefined;
  }

  return step.action;
}

export type GetNextStepFilters =
  | {
      exact?: StepValues;
      omit?: undefined;
    }
  | {
      exact?: undefined;
      omit?: StepValues;
    };

export function getNextStep(
  actionOption: ActionOption | ActionOption[],
  filters: GetNextStepFilters = {},
): NextStep | undefined {
  const actionOptions = Array.isArray(actionOption) ? actionOption : [actionOption];
  const filteredActionOptions = actionOptions.filter((i) => !!i.nextStep);

  if (!filteredActionOptions.length) {
    return undefined;
  }

  if (filters.exact) {
    return filteredActionOptions.find((i) => filters.exact!.includes(i.nextStep!.value))?.nextStep;
  }

  if (filters.omit) {
    return filteredActionOptions.find((i) => !filters.omit!.includes(i.nextStep!.value))?.nextStep;
  }

  return filteredActionOptions[0].nextStep;
}

export function shouldKeepCurrentStep(step: NextStep): boolean {
  return step.value === Step.__STEP__;
}

export function resolveStep(currentStep: NextStep | null, nextStep: NextStep): NextStep | null {
  if (currentStep && shouldKeepCurrentStep(nextStep)) {
    return {
      ...nextStep,
      value: currentStep.value,
    };
  }

  return nextStep ?? null;
}

export type ResolveVerificationParams = {
  payload: Record<string, string>;
  url: string;
  viewType: ViewTypeValues;
};

export function resolveVerificationRedirect({ payload, url, viewType }: ResolveVerificationParams) {
  if (viewType === ViewType.IFRAME) {
    // targetOrigin '*' is intentional when url is not configured — the payload
    // carries no secrets, and the parent needs the message to close the modal.
    window.parent.postMessage(JSON.stringify(payload), url || '*');
  } else {
    if (!url) {
      return;
    }

    window.location.href = `${url}?${new URLSearchParams(payload)}`;
  }
}
